import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { ArrowLeft, ChevronDown, ChevronRight, Search } from "lucide-react";
import DisableReportingToRoleModal from "./DisableReportingToRoleModal";
import HiringManagementEmptyModal from "./HiringManagementEmptyModal";
import AssignableRolesDropdown from "./AssignableRolesDropdown";
import Header from "../Header";
import LeftNavBar from "../LeftNavBar";
import PageHeader from "../PageHeader";
import LoadingView from "../LoadingView";
import FailureView from "../FailureView";
import { PageHeaderName } from "../../StyledCss/styledComponents";
import { PrimaryButton, SecondaryButton } from "../../StyledCss/styledbtn";
import {
  buildMatrixFromResponse,
  getModuleViewFeature,
  normalizeMatrix,
  isCellEditable,
  isBlockedByParent,
  getDefaultRoleAccess,
} from "../../util/permissionMatrixHelpers";
import {
  createRole,
  updateRole,
  duplicateRole,
  getRolePermissionMatrix,
  getRolePermissions,
  saveRolePermissionsForRole,
} from "../../api/services/hroneApi";
import { parseResponseBody } from "../../util/apiResponse";
import { isProtectedRole, isHiddenRole } from "../../util/roleConstants";
import { FEATURE_CODES } from "../../util/featureCodes";
import "./index.css";

const apiStatusConstants = {
  initial: "INITIAL",
  success: "SUCCESS",
  failure: "FAILURE",
  inProgress: "IN_PROGRESS",
};

// Column key used in the matrix while a role doesn't have a real id yet
// (CREATE and DUPLICATE, before the first Save). EDIT always uses the real
// role id as its column key, matching the shared matrix helpers' shape.
const DRAFT_COLUMN = "draft";

// Display order for modules — mirrors the sidebar's navigation order
// (LeftNavBar/navigationConfig.js) instead of whatever order the backend
// happens to return (currently alphabetical), so this screen reads the same
// top-to-bottom order the rest of the app uses. Any module not listed here
// (future modules the backend seeds before this list is updated) sorts
// after all known modules, in whatever order the API returned them.
const MODULE_DISPLAY_ORDER = [
  "Dashboard",
  "Employees",
  "Leave Management",
  "Salary Management",
  "Payslip",
  "Resource Tracking",
  "Reimbursement",
  "Expenditure",
  "Email Templates",
  "Hiring",
  "Policies",
  "Form16",
  "Revenue",
  "Invoices",
  "Projects",
  "Clients",
  "WorkLogs",
  "Profile",
  "Settings",
];

// Toggles that only make sense for a role that can be reported to — they all
// govern acting on another employee's request, which presupposes that people
// report to this role. Hidden (display only) while "Show this role in
// Reporting To" is unchecked; their stored allowed/denied values are never
// touched, so unchecking the box changes nothing about what the role can do.
// Holding either of these means this role can put an employee onto a role, so
// it also needs to be told WHICH roles it may hand out.
const EMPLOYEE_ROLE_ASSIGNING_FEATURE_CODES = [
  FEATURE_CODES.EMPLOYEE_CREATE,
  FEATURE_CODES.EMPLOYEE_EDIT,
];

// One-line hints shown under specific toggles to spell out UI the toggle
// unlocks beyond its obvious action. Display-only — keyed by feature code,
// no effect on what gets saved.
const FEATURE_EXTRA_HINTS = {
  [FEATURE_CODES.HIRING_RESUME_IMPORT]:
    "Also unlocks the Needs Review, Duplicates and Failed tabs on a job.",
};

// The Hiring module is presented behind a single synthetic "management access"
// switch. With it OFF the role keeps only its default Hiring access (the
// non-configurable baseline every role already has) and the individual Hiring
// toggles stay hidden. Turning it ON just reveals those toggles; turning it
// OFF forces every one of them off. This switch is UI-only: it is never a
// feature code, never part of the save payload, and has no backend
// counterpart — it is derived on load from whether the role holds any
// configurable Hiring permission.
const HIRING_MODULE_NAME = "Hiring";

const getHiringModule = (moduleList) =>
  (moduleList || []).find((module) => module.name === HIRING_MODULE_NAME) || null;

const hasAnyHiringPermission = (matrixToCheck, moduleList, colKey) => {
  const hiringModule = getHiringModule(moduleList);
  if (!hiringModule) return false;
  return (hiringModule.features || []).some(
    (feature) => !!matrixToCheck?.[feature.code]?.[colKey]?.allowed,
  );
};

// Children that come on automatically when their module's parent access toggle
// is switched on, keyed by that parent's feature code.
//
// These are the Employees tabs the module is not usable without: the
// Active/Released lists are the page itself, and Overview/Aadhar/PAN are the
// employee-detail tabs. They get no toggle of their own — their stored value
// always follows the parent, and goes to the backend as an ordinary permission
// change. No new feature codes, no special casing server-side.
const AUTO_ENABLED_WITH_MODULE_ACCESS = {
  [FEATURE_CODES.EMPLOYEE_VIEW]: [
    FEATURE_CODES.EMPLOYEE_ACTIVE_LIST_VIEW,
    FEATURE_CODES.EMPLOYEE_RELEASED_LIST_VIEW,
    FEATURE_CODES.EMPLOYEE_OVERVIEW_VIEW,
    FEATURE_CODES.EMPLOYEE_AADHAR_VIEW,
    FEATURE_CODES.EMPLOYEE_PAN_VIEW,
  ],
};

// Flattened lookup of the above: implicit child feature code -> the parent
// access code it follows. Hidden from the permission list and never edited on
// their own.
const IMPLICIT_CHILD_TO_PARENT_CODE = Object.entries(
  AUTO_ENABLED_WITH_MODULE_ACCESS,
).reduce((lookup, [parentCode, childCodes]) => {
  childCodes.forEach((childCode) => {
    lookup[childCode] = parentCode;
  });
  return lookup;
}, {});

// Whether a role holds either Add/Edit Employee permission in `matrix` for
// `columnKey`. The assignable-role selection only means anything while one of
// them is on, so this decides both what loads and what is kept.
const hasEmployeeRoleAssigningPermission = (matrixToCheck, columnKey) =>
  EMPLOYEE_ROLE_ASSIGNING_FEATURE_CODES.some(
    (code) => !!matrixToCheck?.[code]?.[columnKey]?.allowed,
  );

const REPORTING_GATED_FEATURE_CODES = [
  FEATURE_CODES.LEAVE_REQUESTS_VIEW,
  FEATURE_CODES.LEAVE_REQUESTS_APPROVE,
  FEATURE_CODES.LEAVE_REQUESTS_REJECT,
  FEATURE_CODES.REIMBURSEMENT_REQUESTS_VIEW,
  FEATURE_CODES.REIMBURSEMENT_APPROVE,
  FEATURE_CODES.REIMBURSEMENT_REJECT,
];

const CHILD_TO_PARENT_CODE = {
  [FEATURE_CODES.PROJECT_CLONE]: FEATURE_CODES.PROJECT_CREATE,
  [FEATURE_CODES.REIMBURSEMENT_APPROVE]: FEATURE_CODES.REIMBURSEMENT_REQUESTS_VIEW,
  [FEATURE_CODES.REIMBURSEMENT_REJECT]: FEATURE_CODES.REIMBURSEMENT_REQUESTS_VIEW,
  [FEATURE_CODES.POLICY_REQUEST_APPROVE]: FEATURE_CODES.POLICY_REQUEST_VIEW,
  [FEATURE_CODES.POLICY_REQUEST_REJECT]: FEATURE_CODES.POLICY_REQUEST_VIEW,
  [FEATURE_CODES.POLICY_REQUEST_PDF_VIEW]: FEATURE_CODES.POLICY_REQUEST_VIEW,
  [FEATURE_CODES.POLICY_HISTORY_PDF_VIEW]: FEATURE_CODES.POLICY_HISTORY_VIEW,
};

// Reverse lookup: parent code -> the child codes shown under it and switched
// off with it.
const PARENT_TO_CHILD_CODES = Object.entries(CHILD_TO_PARENT_CODE).reduce(
  (parentToChildCodes, [childCode, parentCode]) => {
    if (!parentToChildCodes[parentCode]) parentToChildCodes[parentCode] = [];
    parentToChildCodes[parentCode].push(childCode);
    return parentToChildCodes;
  },
  {},
);

const sortModulesByDisplayOrder = (modules) =>
  [...modules].sort((a, b) => {
    const indexA = MODULE_DISPLAY_ORDER.indexOf(a.name);
    const indexB = MODULE_DISPLAY_ORDER.indexOf(b.name);
    const rankA = indexA === -1 ? MODULE_DISPLAY_ORDER.length : indexA;
    const rankB = indexB === -1 ? MODULE_DISPLAY_ORDER.length : indexB;
    return rankA - rankB;
  });

// Viewport position for the assignable-roles dropdown. It is portalled to
// <body> because .rpe-module-card sets overflow:hidden for its rounded corners
// and collapse animation, which would otherwise clip the panel. Flips above the
// trigger when there isn't room below, mirroring RoleManagementList's row menu.
const ASSIGNABLE_DROPDOWN_HEIGHT = 320;

const getAssignableDropdownPosition = (triggerEl) => {
  const rect = triggerEl.getBoundingClientRect();
  const openUp =
    window.innerHeight - rect.bottom < ASSIGNABLE_DROPDOWN_HEIGHT &&
    rect.top > window.innerHeight - rect.bottom;
  return {
    top: openUp ? "auto" : rect.bottom + 8,
    bottom: openUp ? window.innerHeight - rect.top + 8 : "auto",
    right: Math.max(window.innerWidth - rect.right, 8),
  };
};

const getAccentColor = () => {
  try {
    const org = JSON.parse(localStorage.getItem("org"));
    if (org && org.primaryColor) return org.primaryColor;
  } catch (e) {
    // ignore malformed org data, fall back to default brand color
  }
  return "#e86020";
};

const emptyPermissionsFor = (features) =>
  features.map((feature) => ({
    role_id: DRAFT_COLUMN,
    feature_id: feature.id,
    allowed: false,
    editable: true,
    locked: false,
    reason: null,
  }));

/*
 * Single Permission Configuration screen for CREATE, EDIT, and DUPLICATE.
 * `mode` is the only thing that changes what data seeds the page; the
 * rendering, toggle logic, and Save/Cancel flow are identical across
 * all three — this is the "single place" the role's details and permissions
 * are configured, with no separate rename/edit dialog afterward.
 *
 *   CREATE     route: /role_permissions/new            (no :roleId)
 *   DUPLICATE  route: /role_permissions/:roleId/duplicate (:roleId = SOURCE role, read-only reference)
 *   EDIT       route: /role_permissions/:roleId         (:roleId = the role being edited)
 *
 * CREATE/DUPLICATE hold their draft role+permissions entirely client-side
 * (column key "draft") until Save, which POSTs the role then PUTs the full
 * enabled-permission set. EDIT PUTs only the changed details/permissions
 * against the real role id, exactly as before.
 */
const RolePermissionEditor = ({ mode }) => {
  const { roleId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const accentColor = useMemo(() => getAccentColor(), []);

  const [apiStatus, setApiStatus] = useState(apiStatusConstants.initial);
  const [isSaving, setIsSaving] = useState(false);

  const [roleMeta, setRoleMeta] = useState(null); // { type, employees_assigned, can_edit, can_delete }
  const [roleName, setRoleName] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  // Whether this role should be offered in the Add/Edit Employee "Reporting To"
  // role picker. Persisted on the role itself (roles.show_reporting_to_role),
  // NOT as a module permission — it never enters the permission matrix,
  // buildPermissionChanges, or any of the enabled-permission counts.
  const [showReportingToRole, setShowReportingToRole] = useState(false);
  // Ids of the roles this role may assign on the Add/Edit Employee screens.
  // Persisted on the role itself (role_assignable_roles), not as a permission.
  const [assignableRoleIds, setAssignableRoleIds] = useState([]);
  // The dropdown edits a working copy so Cancel can discard cleanly; OK commits
  // it back to assignableRoleIds.
  const [draftAssignableRoleIds, setDraftAssignableRoleIds] = useState(null);
  // Feature code of the Add/Edit Employee row whose roles dropdown is open,
  // or null when none is.
  const [openAssignableRowCode, setOpenAssignableRowCode] = useState(null);
  // Viewport position for the portalled dropdown panel.
  const [assignableDropdownStyle, setAssignableDropdownStyle] = useState(null);
  // The Add/Edit Employee permission to turn on once OK is pressed, if it
  // isn't on already — set whenever its dropdown opens, from its toggle or
  // its chevron either way.
  const toggleToEnable = useRef(null);
  const assignableDropdownRef = useRef(null);
  const [modules, setModules] = useState([]);
  const [matrix, setMatrix] = useState({});
  // Cross-role snapshot ("which roles currently have this feature enabled"),
  // shown as a small hint under each toggle. Sourced from the multi-role
  // matrix endpoint — same data CREATE mode already fetches for its catalog,
  // just no longer discarded. Best-effort: if this call fails, the hints
  // simply don't render; it never blocks the main single-role editor.
  const [roleAccessMatrix, setRoleAccessMatrix] = useState({ roles: [], matrix: {} });

  const [searchTerm, setSearchTerm] = useState("");
  const [moduleFilter, setModuleFilter] = useState("all");
  // Module id -> collapsed?. Only holds modules the user has actually clicked;
  // anything absent falls back to collapsed (see isModuleCollapsed).
  const [collapsed, setCollapsed] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  // Open only while the user is turning "Show this role in Reporting To" OFF —
  // that also switches the gated request permissions off, so it is confirmed
  // before being staged.
  const [isDisableReportingModalOpen, setIsDisableReportingModalOpen] = useState(false);

  const snapshotRef = useRef({ name: "", description: "", matrix: {} });
  const versionRef = useRef(null);
  const featureIndexRef = useRef(new Map());

  // UI-only: whether the Hiring "management access" section is expanded (see
  // HIRING_MODULE_NAME notes). Derived from the loaded matrix, then
  // user-controllable. Never persisted. The ref keeps the value it had at load
  // / after the last save, so Save can tell "admin just enabled it" apart from
  // "it was already on".
  const [hiringMgmtEnabled, setHiringMgmtEnabled] = useState(false);
  // Open while a Save was blocked because "Hiring management access" is on with
  // nothing selected under it.
  const [isHiringEmptyModalOpen, setIsHiringEmptyModalOpen] = useState(false);

  // A brand-new role has no real id until the first Save, so every toggle
  // is keyed off "draft" until then; EDIT always keys off the real role id.
  const columnKey = mode === "edit" ? roleId : DRAFT_COLUMN;
  const readOnly = mode === "edit" && roleMeta != null && !roleMeta.can_edit;

  // "Which roles currently have this feature enabled" hints, from the
  // multi-role matrix data — either reused from an already-fetched response
  // (CREATE mode fetches this endpoint anyway for its catalog) or fetched
  // fresh (EDIT/DUPLICATE, which load a single role instead). Best-effort:
  // failures here never block the main single-role load, the hints just
  // don't render.
  const applyRoleAccessMatrix = useCallback((data) => {
    const built = buildMatrixFromResponse(data);
    setRoleAccessMatrix({ roles: built.roles, matrix: built.matrix });
  }, []);

  const fetchRoleAccessMatrix = useCallback(async () => {
    try {
      const response = await getRolePermissionMatrix();
      const { ok, data } = await parseResponseBody(response);
      if (ok && data) applyRoleAccessMatrix(data);
    } catch (e) {
      // leave roleAccessMatrix at its previous value; hints just won't show
    }
  }, [applyRoleAccessMatrix]);

  const load = useCallback(async () => {
    setApiStatus(apiStatusConstants.inProgress);
    try {
      if (mode === "create") {
        const draft = location.state?.draftRole || {};
        // No dedicated "blank catalog" endpoint exists — the multi-role
        // matrix endpoint is real/already-integrated and returns the full
        // modules/features catalog regardless of role, so it's reused here
        // purely for that catalog; its `roles`/`permissions` are discarded
        // in favor of an all-off single-column matrix for the new role.
        const response = await getRolePermissionMatrix();
        const { ok, message, data } = await parseResponseBody(response);
        if (!ok) {
          setApiStatus(apiStatusConstants.failure);
          toast.error(message);
          return;
        }
        applyRoleAccessMatrix(data);
        const built = buildMatrixFromResponse({
          roles: [{ id: DRAFT_COLUMN, name: draft.name || "" }],
          modules: data.modules,
          features: data.features,
          permissions: emptyPermissionsFor(data.features),
        });
        const normalized = normalizeMatrix(built.modules, built.matrix);
        setRoleMeta({ type: "Custom", employees_assigned: 0, can_edit: true, can_delete: true });
        setRoleName(draft.name || "");
        setRoleDescription(draft.description || "");
        setShowReportingToRole(false);
        setAssignableRoleIds([]);
        setModules(built.modules);
        setMatrix(normalized);
        // Brand-new role: no Hiring permissions yet, so start collapsed.
        setHiringMgmtEnabled(false);
        featureIndexRef.current = built.featureIndex;
        versionRef.current = null;
        snapshotRef.current = {
          name: draft.name || "",
          description: draft.description || "",
          showReportingToRole: false,
          assignableRoleIds: [],
          matrix: normalized,
        };
      } else if (mode === "duplicate") {
        const response = await getRolePermissions(roleId);
        const { ok, message, data } = await parseResponseBody(response);
        if (!ok) {
          setApiStatus(apiStatusConstants.failure);
          toast.error(message);
          return;
        }
        const built = buildMatrixFromResponse(data);
        const sourceColumnId = data.role.id;
        const remapped = {};
        Object.keys(built.matrix).forEach((code) => {
          remapped[code] = { [DRAFT_COLUMN]: built.matrix[code][sourceColumnId] };
        });
        const normalized = normalizeMatrix(built.modules, remapped);
        const draftName = `${data.role.name} Copy`;
        setRoleMeta({ type: "Custom", employees_assigned: 0, can_edit: true, can_delete: true });
        setRoleName(draftName);
        setRoleDescription(data.role.description || "");
        setShowReportingToRole(!!data.role.show_reporting_to_role);
        // The stored selection only applies while the role actually holds
        // Add/Edit Employee. A role with neither loads with nothing selected,
        // so switching one on opens the picker clean rather than pre-ticked
        // with a set the role cannot currently use.
        const duplicateAssignableRoleIds = hasEmployeeRoleAssigningPermission(
          normalized, DRAFT_COLUMN,
        )
          ? data.assignable_role_ids || []
          : [];
        setAssignableRoleIds(duplicateAssignableRoleIds);
        setModules(built.modules);
        setMatrix(normalized);
        {
          const dupHiringMgmt = hasAnyHiringPermission(normalized, built.modules, DRAFT_COLUMN);
          setHiringMgmtEnabled(dupHiringMgmt);
        }
        featureIndexRef.current = built.featureIndex;
        versionRef.current = null;
        snapshotRef.current = {
          name: draftName,
          description: data.role.description || "",
          showReportingToRole: !!data.role.show_reporting_to_role,
          assignableRoleIds: duplicateAssignableRoleIds,
          matrix: normalized,
        };
        fetchRoleAccessMatrix();
      } else {
        const response = await getRolePermissions(roleId);
        const { ok, message, data } = await parseResponseBody(response);
        if (!ok) {
          setApiStatus(apiStatusConstants.failure);
          toast.error(message);
          return;
        }
        // Super Admin and Admin have nothing to configure, so this screen is
        // not opened for them at all — not even read-only. The Role Management
        // list withholds the entry point; this guards a pasted/bookmarked URL.
        if (isProtectedRole(data.role.name)) {
          toast.error(`${data.role.name} permissions cannot be edited.`);
          navigate("/role_permissions", { replace: true });
          return;
        }
        const built = buildMatrixFromResponse(data);
        const normalized = normalizeMatrix(built.modules, built.matrix);
        setRoleMeta(data.role);
        setRoleName(data.role.name);
        setRoleDescription(data.role.description || "");
        setShowReportingToRole(!!data.role.show_reporting_to_role);
        // Same rule as DUPLICATE above: no Add/Edit Employee permission means
        // no selection to show.
        const editAssignableRoleIds = hasEmployeeRoleAssigningPermission(normalized, roleId)
          ? data.assignable_role_ids || []
          : [];
        setAssignableRoleIds(editAssignableRoleIds);
        setModules(built.modules);
        setMatrix(normalized);
        {
          const editHiringMgmt = hasAnyHiringPermission(normalized, built.modules, roleId);
          setHiringMgmtEnabled(editHiringMgmt);
        }
        featureIndexRef.current = built.featureIndex;
        versionRef.current = data?.version ?? null;
        snapshotRef.current = {
          name: data.role.name,
          description: data.role.description || "",
          showReportingToRole: !!data.role.show_reporting_to_role,
          assignableRoleIds: editAssignableRoleIds,
          matrix: normalized,
        };
        fetchRoleAccessMatrix();
      }
      setIsDirty(false);
      setApiStatus(apiStatusConstants.success);
    } catch (err) {
      toast.error("Unable to reach the server. Please try again.");
      setApiStatus(apiStatusConstants.failure);
    }
    // location.state is only read once on the initial CREATE load — excluded
    // from deps so re-navigating within the same session doesn't re-trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, roleId]);

  useEffect(() => {
    load();
  }, [load]);

  // Close the assignable-roles dropdown on an outside click. A pending enable
  // is abandoned with it, so the permission stays off — same as Cancel.
  useEffect(() => {
    if (!openAssignableRowCode) return undefined;
    const close = () => {
      setOpenAssignableRowCode(null);
      setDraftAssignableRoleIds(null);
      setAssignableDropdownStyle(null);
      toggleToEnable.current = null;
    };
    const onMouseDown = (event) => {
      if (assignableDropdownRef.current?.contains(event.target)) return;
      if (event.target.closest?.(".rpe-assignable-dropdown-wrap")) return;
      close();
    };
    // The panel is position:fixed, so it would drift away from its row when the
    // PAGE scrolls — close it instead, like the Role Management row menu does.
    // Registered with capture so scrolls on any ancestor are caught, which also
    // catches scrolling the panel's OWN role list: that must not close it.
    const onScroll = (event) => {
      if (assignableDropdownRef.current?.contains(event.target)) return;
      close();
    };
    document.addEventListener("mousedown", onMouseDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", close);
    };
  }, [openAssignableRowCode]);

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const orderedModules = useMemo(() => sortModulesByDisplayOrder(modules), [modules]);

  // Name of the role being edited, as it appears in the multi-role snapshot
  // (roleAccessMatrix) — only resolvable in EDIT mode, since CREATE/DUPLICATE
  // work against a "draft" role that doesn't exist in that snapshot yet.
  // Used below to patch the snapshot's stale "currently accessible to" hint
  // with this role's live (unsaved) toggle state.
  const editedRoleName = useMemo(() => {
    if (mode !== "edit") return null;
    return roleAccessMatrix.roles.find((r) => String(r.id) === String(roleId))?.name ?? null;
  }, [mode, roleId, roleAccessMatrix.roles]);

  // Every role in the tenant, from the multi-role snapshot this screen already
  // loads for its "Currently accessible to" hints — no extra request. Super
  // Admin is withheld (see roleConstants.js): it is never a role another
  // role should be handing out.
  const assignableRoleOptions = useMemo(
    () =>
      roleAccessMatrix.roles
        .filter((r) => !isHiddenRole(r.name))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [roleAccessMatrix.roles],
  );

  const closeAssignableDropdown = () => {
    setOpenAssignableRowCode(null);
    setDraftAssignableRoleIds(null);
    setAssignableDropdownStyle(null);
    toggleToEnable.current = null;
  };

  // Opened from the chevron on an Add/Edit Employee row, to review or change
  // the roles — whether the permission is already on, or still off and this
  // is how the admin chose to turn it on (see confirmAssignableRoles).
  const openAssignableDropdown = (module, feature, triggerEl) => {
    if (readOnly) return;
    const featureCode = feature.code;
    if (openAssignableRowCode === featureCode) {
      closeAssignableDropdown();
      return;
    }
    toggleToEnable.current = { module, feature };
    setDraftAssignableRoleIds(assignableRoleIds);
    setAssignableDropdownStyle(getAssignableDropdownPosition(triggerEl));
    setOpenAssignableRowCode(featureCode);
  };

  const toggleDraftAssignableRole = (roleIdToToggle) => {
    setDraftAssignableRoleIds((prev) =>
      prev.includes(roleIdToToggle)
        ? prev.filter((id) => id !== roleIdToToggle)
        : [...prev, roleIdToToggle],
    );
  };

  // OK: at least one role is required. A permission that can assign nothing is
  // not a usable permission, so with an empty selection the choice is refused
  // and the permission's toggle — if still off — stays off rather than ending
  // up enabled with nothing behind it.
  const confirmAssignableRoles = () => {
    if (!draftAssignableRoleIds || draftAssignableRoleIds.length === 0) {
      toast.error("Select at least one role this role can assign.");
      return;
    }

    setAssignableRoleIds(draftAssignableRoleIds);
    setIsDirty(true);

    // Turn this permission on if it isn't already — covers both the
    // toggle-click-while-off flow and picking roles via the chevron while
    // the permission was still off. A permission reviewed via the chevron
    // while already on is left untouched.
    const target = toggleToEnable.current;
    if (target) {
      const permissionCell = matrix[target.feature.code]?.[columnKey];
      const isPermissionOn = permissionCell?.allowed;
      if (!isPermissionOn) {
        applyPermissionToggle(target.module, target.feature);
      }
    }
    closeAssignableDropdown();
  };

  // Cancel: throw the working copy away. If this dropdown was opened by
  // switching a permission on, that permission is simply never enabled.
  const cancelAssignableRoles = () => closeAssignableDropdown();

  const visibleModules = useMemo(() => {
    return orderedModules
      .filter((module) => moduleFilter === "all" || module.id === moduleFilter)
      .map((module) => {
        const viewFeature = getModuleViewFeature(module);
        let features = (module.features || [])
          // Implicit children have no toggle of their own — they follow their
          // module's parent access toggle. Hidden from the list, still saved.
          .filter((feature) => !IMPLICIT_CHILD_TO_PARENT_CODE[feature.code])
          // Child toggles (e.g. Clone Project) are rendered as a sub-row under
          // their parent toggle, not as a row of their own.
          .filter((feature) => !CHILD_TO_PARENT_CODE[feature.code])
          .filter(
            (feature) =>
              showReportingToRole || !REPORTING_GATED_FEATURE_CODES.includes(feature.code),
          )
          .filter((feature) => {
            if (!normalizedSearch) return true;
            if (
              feature.display_name?.toLowerCase().includes(normalizedSearch) ||
              feature.code?.toLowerCase().includes(normalizedSearch) ||
              module.name?.toLowerCase().includes(normalizedSearch)
            ) {
              return true;
            }
            // A parent toggle's own name might not match (e.g. "View Policy
            // Requests" vs a search for "approve"), but one of its nested
            // child toggles (Approve/Reject/View, rendered only under this
            // parent — see CHILD_TO_PARENT_CODE) could. Keep the parent
            // visible whenever a child matches: a child toggle can only be
            // turned on while its parent is on, so hiding the parent would
            // leave the matching child with no way to be enabled or disabled.
            const childCodes = PARENT_TO_CHILD_CODES[feature.code] || [];
            return childCodes.some((childCode) => {
              const child = module.features?.find((f) => f.code === childCode);
              return (
                child?.display_name?.toLowerCase().includes(normalizedSearch) ||
                child?.code?.toLowerCase().includes(normalizedSearch)
              );
            });
          })
          // When search matches child permissions, keep the module-level parent
        // toggle visible so hierarchy and parent/child toggle behavior persist.
        if (normalizedSearch && viewFeature) {
          const hasViewFeature = features.some((feature) => feature.code === viewFeature.code);
          const hasChildFeatures = features.some((feature) => feature.code !== viewFeature.code);
          if (hasChildFeatures && !hasViewFeature) {
            features = [viewFeature, ...features];
          }
        }
        features = features.sort((a, b) => {
            if (viewFeature && a.code === viewFeature.code) return -1;
            if (viewFeature && b.code === viewFeature.code) return 1;
            return 0;
          });
        return { ...module, features };
      })
      .filter((module) => module.features.length > 0);
  }, [orderedModules, moduleFilter, normalizedSearch, showReportingToRole]);

  const handleNameChange = (e) => {
    setRoleName(e.target.value);
    setIsDirty(true);
  };

  const handleDescriptionChange = (e) => {
    setRoleDescription(e.target.value);
    setIsDirty(true);
  };

  // Display names of the reporting-gated permissions this role currently HAS —
  // i.e. exactly the ones unchecking the box would switch off. Anything
  // already off is left out, so the confirmation never claims it will disable
  // something that was never enabled. Recomputed from the live matrix, so it
  // reflects unsaved toggle changes too.
  const reportingTogglesToBeDisabled = useMemo(() => {
    const names = [];
    modules.forEach((module) => {
      (module.features || []).forEach((feature) => {
        if (
          REPORTING_GATED_FEATURE_CODES.includes(feature.code) &&
          matrix[feature.code]?.[columnKey]?.allowed
        ) {
          names.push(`${module.name} — ${feature.display_name}`);
        }
      });
    });
    return names;
  }, [modules, matrix, columnKey]);

  // Switch every reporting-gated permission off for this role. Returns a new
  // matrix; the stored values of every other feature are untouched.
  const withReportingGatedPermissionsDisabled = useCallback(
    (currentMatrix) => {
      const updated = { ...currentMatrix };
      REPORTING_GATED_FEATURE_CODES.forEach((featureCode) => {
        const cell = updated[featureCode]?.[columnKey];
        if (!cell || !cell.allowed) return;
        updated[featureCode] = {
          ...updated[featureCode],
          [columnKey]: { ...cell, allowed: false },
        };
      });
      return normalizeMatrix(modules, updated);
    },
    [columnKey, modules],
  );

  // Saved alongside the other role details (name/description) by
  // handleSave — the flag itself is a column on the role, not a permission.
  // Turning it ON is immediate; turning it OFF also switches the gated
  // request permissions off, so it goes through a confirmation first.
  const handleShowReportingToRoleChange = (e) => {
    if (e.target.checked) {
      setShowReportingToRole(true);
      setIsDirty(true);
      return;
    }
    setIsDisableReportingModalOpen(true);
  };

  // Confirmed in DisableReportingToRoleModal. Stages both halves of the
  // change locally — nothing is persisted until Save Changes, which sends the
  // flag with the role details and the switched-off permissions through the
  // usual permission diff.
  const confirmDisableReportingToRole = () => {
    setShowReportingToRole(false);
    setMatrix((prev) => withReportingGatedPermissionsDisabled(prev));
    setIsDirty(true);
    setIsDisableReportingModalOpen(false);
  };

  // Cancelled: the checkbox stays on and no permission is touched.
  const cancelDisableReportingToRole = () => setIsDisableReportingModalOpen(false);

  // Performs the actual flip. Split out from togglePermission so the roles
  // dropdown can complete a deferred enable once a role has been picked.
  const applyPermissionToggle = (module, feature) => {
    const featureCode = feature.code;
    const cell = matrix[featureCode]?.[columnKey];
    if (!cell) return;
    const nextAllowed = !cell.allowed;
    setMatrix((prev) => {
      const toggled = {
        ...prev,
        [featureCode]: {
          ...prev[featureCode],
          [columnKey]: { ...cell, allowed: nextAllowed },
        },
      };

      // Switching a module's parent access ON brings its implicit children
      // with it. Children already on are left alone, and codes this role's
      // catalog doesn't include are skipped.
      if (nextAllowed) {
        (AUTO_ENABLED_WITH_MODULE_ACCESS[featureCode] || []).forEach((childCode) => {
          const childCell = toggled[childCode]?.[columnKey];
          if (!childCell || childCell.allowed) return;
          toggled[childCode] = {
            ...toggled[childCode],
            [columnKey]: { ...childCell, allowed: true },
          };
        });
      }

      // Switching a parent toggle OFF takes its child toggles with it —
      // Clone Project can't stand on its own once Add Project is gone.
      if (!nextAllowed) {
        (PARENT_TO_CHILD_CODES[featureCode] || []).forEach((childCode) => {
          const childCell = toggled[childCode]?.[columnKey];
          if (!childCell || !childCell.allowed) return;
          toggled[childCode] = {
            ...toggled[childCode],
            [columnKey]: { ...childCell, allowed: false },
          };
        });
      }

      return normalizeMatrix(modules, toggled);
    });
    setIsDirty(true);

    // Keep the synthetic Hiring switch in sync: turning any Hiring toggle on
    // (e.g. from a search hit while the section is collapsed) implies the
    // section is active.
    if (nextAllowed && HIRING_MODULE_NAME === module.name && !hiringMgmtEnabled) {
      setHiringMgmtEnabled(true);
    }

    // Losing the last of the two permissions clears the choice, so switching
    // one back on never reopens pre-ticked with a set the role no longer has.
    // Only the LAST one clears it: turning Add Employee off while Edit Employee
    // is still on must not wipe the list Edit is still using.
    //
    // The module's own access toggle counts too — turning it off cascades both
    // children off via normalizeMatrix, so the choice goes with them.
    const viewFeature = getModuleViewFeature(module);
    const isModuleAccessToggle = viewFeature && viewFeature.code === featureCode;
    const affectsAssigning =
      isModuleAccessToggle ||
      EMPLOYEE_ROLE_ASSIGNING_FEATURE_CODES.includes(featureCode);

    if (affectsAssigning && !nextAllowed) {
      const stillAssigning = EMPLOYEE_ROLE_ASSIGNING_FEATURE_CODES.some((code) => {
        if (isModuleAccessToggle) return false; // parent off cascades both off
        if (code === featureCode) return false; // the one just switched off
        return !!matrix[code]?.[columnKey]?.allowed;
      });
      if (!stillAssigning && assignableRoleIds.length > 0) {
        setAssignableRoleIds([]);
      }
    }
  };

  const togglePermission = (module, feature, triggerEl) => {
    if (readOnly) return;
    const featureCode = feature.code;
    const cell = matrix[featureCode]?.[columnKey];
    if (!isCellEditable(matrix, cell, module, feature, columnKey)) return;

    // Switching Add/Edit Employee ON asks which roles it may hand out FIRST and
    // only enables the permission once at least one is chosen — the permission
    // is meaningless without that scope, and a toggle that is on while the role
    // can assign nothing would be a lie. Seeded from the committed choice,
    // which is empty whenever the permission has been off.
    if (EMPLOYEE_ROLE_ASSIGNING_FEATURE_CODES.includes(featureCode) && !cell.allowed) {
      toggleToEnable.current = { module, feature };
      setDraftAssignableRoleIds(assignableRoleIds);
      if (triggerEl) setAssignableDropdownStyle(getAssignableDropdownPosition(triggerEl));
      setOpenAssignableRowCode(featureCode);
      return;
    }

    // A child toggle (e.g. Clone Project) can't be switched on while its
    // parent (Add Project) is off — its sub-row toggle is disabled in that
    // state, and this is the matching guard. Turning the parent off cascades
    // the child off silently in applyPermissionToggle.
    const parentCode = CHILD_TO_PARENT_CODE[featureCode];
    if (parentCode && !cell.allowed && !matrix[parentCode]?.[columnKey]?.allowed) return;

    applyPermissionToggle(module, feature);
  };

  // The synthetic Hiring "management access" switch. ON just reveals the real
  // Hiring toggles (no matrix change). OFF forces every configurable Hiring
  // permission off for this role. Never persisted on its own.
  const handleHiringMgmtToggle = () => {
    if (readOnly) return;
    if (hiringMgmtEnabled) {
      const hiringModule = getHiringModule(modules);
      setMatrix((prev) => {
        // Only the Hiring feature codes are touched — every other module's
        // cells are left exactly as the admin set them. (No normalizeMatrix
        // pass needed: Hiring has no parent feature in the matrix, and the
        // rest of the matrix is already normalized from prior toggles.)
        let next = prev;
        (hiringModule?.features || []).forEach((feature) => {
          const cell = next[feature.code]?.[columnKey];
          if (!cell?.allowed) return;
          if (next === prev) next = { ...prev };
          next[feature.code] = {
            ...next[feature.code],
            [columnKey]: { ...cell, allowed: false },
          };
        });
        return next;
      });
      setHiringMgmtEnabled(false);
    } else {
      setHiringMgmtEnabled(true);
    }
    setIsDirty(true);
  };

  // "OK" on the blocked-save modal: switch the Hiring section off (it is
  // currently on) and close. The admin then Saves again — nothing to persist
  // for Hiring, everything else goes through as usual.
  const confirmTurnOffHiringManagement = () => {
    setIsHiringEmptyModalOpen(false);
    if (hiringMgmtEnabled) handleHiringMgmtToggle();
  };

  // Modules start closed — the full permission list is long, so the user opens
  // just the ones they need. An active search overrides that, since matches
  // inside a closed card are invisible; clearing the search restores whatever
  // the user had opened by hand.
  const isModuleCollapsed = (moduleId) =>
    normalizedSearch ? false : collapsed[moduleId] ?? true;

  const toggleCollapse = (moduleId) => {
    setCollapsed((prev) => ({ ...prev, [moduleId]: !(prev[moduleId] ?? true) }));
  };

  // EDIT: only the cells that changed since the last load/save (diff-based
  // PUT). CREATE/DUPLICATE: send an explicit allowed state for EVERY feature
  // (not just the enabled ones) so the new role's permissions exactly match
  // what's on screen — important for DUPLICATE specifically, since the
  // backend's duplicate endpoint may already copy the source role's full
  // permission set server-side, and any edits the user made to the pre-fill
  // before Save need to fully overwrite that, not just layer partial changes
  // on top of an unknown server-side default.
  // The value to persist for one feature. An implicit child has no toggle of
  // its own, so it always takes its parent access toggle's value — which also
  // repairs any existing role whose parent is on but whose hidden children were
  // left off, on its next save.
  const resolveAllowed = useCallback(
    (featureCode) => {
      const parentCode = IMPLICIT_CHILD_TO_PARENT_CODE[featureCode];
      const effectiveCode = parentCode || featureCode;
      return !!matrix[effectiveCode]?.[columnKey]?.allowed;
    },
    [matrix, columnKey],
  );

  const buildPermissionChanges = useCallback(() => {
    if (mode === "edit") {
      const pristine = snapshotRef.current.matrix || {};
      const changes = [];
      Object.keys(matrix).forEach((featureCode) => {
        const feature = featureIndexRef.current.get(featureCode);
        if (!feature) return;
        const currentAllowed = resolveAllowed(featureCode);
        const pristineAllowed = !!pristine[featureCode]?.[columnKey]?.allowed;
        if (currentAllowed !== pristineAllowed) {
          changes.push({ feature_id: feature.id, allowed: currentAllowed });
        }
      });
      return changes;
    }

    const changes = [];
    Object.keys(matrix).forEach((featureCode) => {
      const feature = featureIndexRef.current.get(featureCode);
      if (!feature) return;
      changes.push({ feature_id: feature.id, allowed: resolveAllowed(featureCode) });
    });
    return changes;
  }, [matrix, mode, columnKey, resolveAllowed]);

  // Order-independent comparison for the assignable-role selection, so simply
  // reordering the checkboxes never counts as an unsaved change.
  const isSameRoleIdSet = (a, b) =>
    a.length === b.length && [...a].sort().join("|") === [...b].sort().join("|");

  const handleCancel = () => navigate("/role_permissions");

  // Save is the single action that persists both Role Details and
  // Permissions together, regardless of mode — CREATE/DUPLICATE POST the
  // role then PUT its initial permissions; EDIT PUTs whatever changed.
  const handleSave = async () => {
    const trimmedName = roleName.trim();
    if (!trimmedName) {
      toast.error("Role name is required.");
      return;
    }

    // "Hiring management access" is on but nothing under it is selected — the
    // save would give the role nothing extra. Hold it back and explain; the
    // admin either picks a permission or switches the section off.
    const hiringModule = getHiringModule(modules);
    const anyHiringSelected = (hiringModule?.features || []).some((f) => resolveAllowed(f.code));
    if (hiringMgmtEnabled && !anyHiringSelected) {
      setIsHiringEmptyModalOpen(true);
      return;
    }

    setIsSaving(true);
    try {
      let targetRoleId = roleId;
      let targetVersion = versionRef.current;

      if (mode === "edit") {
        const detailsChanged =
          trimmedName !== snapshotRef.current.name ||
          roleDescription.trim() !== snapshotRef.current.description ||
          showReportingToRole !== snapshotRef.current.showReportingToRole ||
          !isSameRoleIdSet(assignableRoleIds, snapshotRef.current.assignableRoleIds || []);
        if (detailsChanged) {
          const updateResponse = await updateRole(roleId, {
            name: trimmedName,
            description: roleDescription,
            showReportingToRole,
            assignableRoleIds,
          });
          const { ok, message } = await parseResponseBody(updateResponse);
          if (!ok) {
            toast.error(message);
            return;
          }

          // The permission version token is a hash of this role's mappings AND
          // its modified_at, and the update above just bumped modified_at — so
          // the token captured at page load is now stale and the permission
          // save below would be rejected as a 409 conflict. Re-read the fresh
          // token first. This matters most for the "Show this role in
          // Reporting To" checkbox, which changes role details and permissions
          // in the same Save every time it is switched off.
          const refreshedResponse = await getRolePermissions(roleId);
          const refreshed = await parseResponseBody(refreshedResponse);
          if (refreshed.ok) {
            targetVersion = refreshed.data?.version ?? targetVersion;
          }
        }
      } else if (mode === "duplicate") {
        // roleId here is the SOURCE role being copied, per the
        // POST /roles/{roleId}/duplicate contract.
        const duplicateResponse = await duplicateRole(roleId, {
          name: trimmedName,
          description: roleDescription,
        });
        const { ok, message, data } = await parseResponseBody(duplicateResponse);
        if (!ok) {
          toast.error(message);
          return;
        }
        targetRoleId = data.id;
        targetVersion = null;

        // /duplicate only copies name/description, so the reporting-structure
        // and assignable-role choices made in this form have to be persisted
        // separately.
        const duplicateSettingsResponse = await updateRole(targetRoleId, {
          name: trimmedName,
          description: roleDescription,
          showReportingToRole,
          assignableRoleIds,
        });
        const duplicateSettingsResult = await parseResponseBody(duplicateSettingsResponse);
        if (!duplicateSettingsResult.ok) {
          toast.error(duplicateSettingsResult.message);
          return;
        }
      } else {
        const createResponse = await createRole({
          name: trimmedName,
          description: roleDescription,
          showReportingToRole,
          assignableRoleIds,
        });
        const { ok, message, data } = await parseResponseBody(createResponse);
        if (!ok) {
          toast.error(message);
          return;
        }
        targetRoleId = data.id;
        targetVersion = null;
      }

      const changes = buildPermissionChanges().map((change) => ({
        ...change,
        role_id: targetRoleId,
      }));

      if (changes.length > 0) {
        const saveResponse = await saveRolePermissionsForRole(targetRoleId, {
          version: targetVersion,
          changes,
        });
        const { ok, message, data } = await parseResponseBody(saveResponse);
        if (!ok) {
          toast.error(message);
          return;
        }
        targetVersion = data?.version ?? targetVersion;
      }

      toast.success(mode === "edit" ? "Role updated successfully." : "Role created successfully.");
      setSearchTerm("");

      if (mode === "edit") {
        versionRef.current = targetVersion;
        snapshotRef.current = {
          name: trimmedName,
          description: roleDescription.trim(),
          showReportingToRole,
          assignableRoleIds,
          matrix,
        };
        setIsDirty(false);
        // Refresh the "Currently accessible to" hints — they're sourced from
        // the multi-role matrix snapshot fetched on load, which is now stale
        // for whichever permissions this save just changed.
        fetchRoleAccessMatrix();
      } else {
        // Now that the role is real, land on its EDIT url so further changes
        // save in place instead of creating another role.
        navigate(`/role_permissions/${targetRoleId}`, { replace: true });
      }
    } catch (err) {
      console.error("RolePermissionEditor handleSave failed:", err);
      toast.error("Unable to reach the server. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const { modulesEnabledCount, permissionsEnabledCount, totalPermissionsCount } = useMemo(() => {
    let modulesEnabled = 0;
    let permissionsEnabled = 0;
    let total = 0;
    modules.forEach((module) => {
      let moduleHasAny = false;
      module.features.forEach((feature) => {
        // Implicit children (e.g. the Employees tabs auto-granted with module
        // access) have no toggle of their own, so they're excluded here too —
        // this counter should only ever move by what a toggle on screen moved.
        if (IMPLICIT_CHILD_TO_PARENT_CODE[feature.code]) return;
        total += 1;
        if (resolveAllowed(feature.code)) {
          permissionsEnabled += 1;
          moduleHasAny = true;
        }
      });
      if (moduleHasAny) modulesEnabled += 1;
    });
    return {
      modulesEnabledCount: modulesEnabled,
      permissionsEnabledCount: permissionsEnabled,
      totalPermissionsCount: total,
    };
    // resolveAllowed already closes over matrix and columnKey.
  }, [modules, resolveAllowed]);

  const pageTitle =
    mode === "edit" ? "Edit Role Permissions" : mode === "duplicate" ? "Duplicate Role" : "Create Role";
  // Deliberately doesn't gate on name validity — the button stays clickable so
  // an empty name surfaces its error via handleSave's toast, instead of
  // leaving the button disabled with no visible reason why.
  const canSave = !readOnly && (mode !== "edit" || isDirty);

  const renderEditor = () => (
    <>
      {isDisableReportingModalOpen && (
        <DisableReportingToRoleModal
          roleName={roleName.trim()}
          affectedToggleNames={reportingTogglesToBeDisabled}
          onCancel={cancelDisableReportingToRole}
          onConfirm={confirmDisableReportingToRole}
        />
      )}

      {isHiringEmptyModalOpen && (
        <HiringManagementEmptyModal
          roleName={roleName.trim()}
          onCancel={() => setIsHiringEmptyModalOpen(false)}
          onConfirm={confirmTurnOffHiringManagement}
        />
      )}

      <nav className="rpe-breadcrumb">
        <Link to="/role_permissions">Role Management</Link>
        <ChevronRight size={14} />
        <span>{roleName || "New Role"}</span>
        <ChevronRight size={14} />
        <span className="rpe-breadcrumb-current">Permissions</span>
      </nav>

      <div className="rpe-role-heading">
        <div className="rpe-field">
          <label htmlFor="role-name">Role Name</label>
          <input
            id="role-name"
            type="text"
            value={roleName}
            onChange={handleNameChange}
            disabled={readOnly}
            placeholder="Enter role name"
          />
        </div>
        <div className="rpe-field">
          <label htmlFor="role-description">Description</label>
          <textarea
            id="role-description"
            value={roleDescription}
            onChange={handleDescriptionChange}
            disabled={readOnly}
            placeholder="Enter role description (optional)"
            rows={2}
          />
        </div>
        <div className="rpe-field rpe-field-checkbox">
          <label className="rpe-checkbox-label" htmlFor="role-show-reporting-to-role">
            <input
              id="role-show-reporting-to-role"
              type="checkbox"
              checked={showReportingToRole}
              onChange={handleShowReportingToRoleChange}
              disabled={readOnly}
            />
            <span>Show this role in Reporting To</span>
          </label>
          <span className="rpe-field-hint">
            When enabled, users with this role can be selected in the "Reporting To" role field while adding or editing an employee. The related Leave and Reimbursement request permissions also become available below, and can be turned on individually.
          </span>
        </div>
      </div>

      <div className="rpe-summary-card">
        <div className="rpe-summary-item">
          <span className="rpe-summary-label">Role Type</span>
          <span className="rpe-summary-value">{roleMeta?.type}</span>
        </div>
        <div className="rpe-summary-item">
          <span className="rpe-summary-label">Employees Assigned</span>
          <span className="rpe-summary-value">{roleMeta?.employees_assigned ?? 0}</span>
        </div>
        <div className="rpe-summary-item">
          <span className="rpe-summary-label">Modules Enabled</span>
          <span className="rpe-summary-value">
            {modulesEnabledCount} / {modules.length}
          </span>
        </div>
        <div className="rpe-summary-item">
          <span className="rpe-summary-label">Permissions Enabled</span>
          <span className="rpe-summary-value">
            {permissionsEnabledCount} / {totalPermissionsCount}
          </span>
        </div>
      </div>

      {readOnly && (
        <div className="rpe-readonly-banner">
          This is a system role. Its permissions are viewable but cannot be edited.
        </div>
      )}

      <div className="rpe-toolbar">
        <div className="rpe-search">
          <Search size={16} className="rpe-search-icon" />
          <input
            type="text"
            className="rpe-search-input"
            placeholder="Search permissions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select
          className="rpe-filter-select"
          value={moduleFilter}
          onChange={(e) => setModuleFilter(e.target.value)}
        >
          <option value="all">All Modules</option>
          {orderedModules.map((module) => (
            <option key={module.id} value={module.id}>
              {module.name}
            </option>
          ))}
        </select>
      </div>

      <div className="rpe-modules">
        {modules.length > 0 && visibleModules.length === 0 && (
          <div className="rpe-empty">No permissions match your search.</div>
        )}

        {visibleModules.map((module) => {
          const isCollapsed = isModuleCollapsed(module.id);
          const viewFeature = getModuleViewFeature(module);
          const enabledCount = module.features.filter(
            (feature) => matrix[feature.code]?.[columnKey]?.allowed,
          ).length;
          const isHiringModule = module.name === HIRING_MODULE_NAME;
          // Hiring's individual toggles are hidden until the synthetic
          // "management access" switch is on (a search always reveals them).
          const showHiringChildren =
            !isHiringModule || hiringMgmtEnabled || !!normalizedSearch;

          return (
            <div className="rpe-module-card" key={module.id}>
              <button
                type="button"
                className="rpe-module-header"
                onClick={() => toggleCollapse(module.id)}
              >
                <span
                  className={`rpe-module-toggle ${isCollapsed ? "rpe-module-toggle-collapsed" : ""}`}
                >
                  <ChevronDown size={16} />
                </span>
                <span className="rpe-module-name">{module.name}</span>
                <span className="rpe-module-count">
                  {isHiringModule && !hiringMgmtEnabled
                    ? "Default"
                    : `${enabledCount}/${module.features.length}`}
                </span>
              </button>

              <div
                className={`rpe-module-body ${isCollapsed ? "rpe-module-body-collapsed" : ""}`}
              >
                {isHiringModule && (
                  <div className="rpe-perm-row rpe-perm-row-parent">
                    <div className="rpe-perm-info">
                      <span className="rpe-perm-name">Hiring management access</span>
                      <span className="rpe-perm-parent-sublabel">
                        Off — this role keeps default Hiring access only. On — choose which job and candidate actions it can perform below.
                      </span>
                    </div>
                    <div className="rpe-perm-controls">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={hiringMgmtEnabled}
                        aria-label="Hiring management access"
                        disabled={readOnly}
                        className={`rpe-switch ${hiringMgmtEnabled ? "rpe-switch-on" : ""} ${
                          readOnly ? "rpe-switch-disabled" : ""
                        }`}
                        onClick={handleHiringMgmtToggle}
                      >
                        <span className="rpe-switch-knob" />
                      </button>
                    </div>
                  </div>
                )}
                {isHiringModule && !showHiringChildren && (
                  <div className="rpe-perm-row rpe-perm-row-child">
                    <div className="rpe-perm-info">
                      <span className="rpe-perm-role-access">
                        Default Hiring access applies. Turn on management access to configure permissions.
                      </span>
                    </div>
                  </div>
                )}
                {showHiringChildren && module.features.map((feature) => {
                    const isViewFeature = viewFeature && feature.code === viewFeature.code;
                    const cell = matrix[feature.code]?.[columnKey];
                    const editable =
                      !readOnly && isCellEditable(matrix, cell, module, feature, columnKey);
                    const blockedByParent = isBlockedByParent(matrix, module, feature, columnKey);
                    const tooltip =
                      cell?.reason ||
                      (blockedByParent ? `Enable "${viewFeature.display_name}" first` : undefined);
                    const persistedAllowedRoles = getDefaultRoleAccess(
                      roleAccessMatrix.matrix,
                      roleAccessMatrix.roles,
                      feature.code,
                    );
                    // roleAccessMatrix is a point-in-time snapshot (fetched on
                    // load/save), so it doesn't know about toggles made since
                    // then — patch in this role's live `cell.allowed` state so
                    // the hint updates immediately as the switch is flipped,
                    // instead of only after Save.
                    const currentlyAllowedRoles = editedRoleName
                      ? cell?.allowed
                        ? Array.from(new Set([...persistedAllowedRoles, editedRoleName])).sort()
                        : persistedAllowedRoles.filter((name) => name !== editedRoleName)
                      : persistedAllowedRoles;

                    // Child toggles shown as an indented sub-row right below
                    // this one (e.g. Clone Project under Add Project). Under
                    // an active search, only show a child that itself matches
                    // — strictly, regardless of whether the parent's own name
                    // also happens to match (e.g. "View Policy Requests"
                    // contains "view", but that shouldn't pull in
                    // "Approve"/"Reject" when searching "view").
                    const childFeatures = (PARENT_TO_CHILD_CODES[feature.code] || [])
                      .map((code) => featureIndexRef.current.get(code))
                      .filter(Boolean)
                      .filter((childFeature) => {
                        if (!normalizedSearch) return true;
                        return (
                          childFeature.display_name?.toLowerCase().includes(normalizedSearch) ||
                          childFeature.code?.toLowerCase().includes(normalizedSearch)
                        );
                      });

                    return (
                      <React.Fragment key={feature.id}>
                      <div
                        className={`rpe-perm-row ${isViewFeature ? "rpe-perm-row-parent" : ""} ${
                          viewFeature && !isViewFeature ? "rpe-perm-row-child" : ""
                        } ${blockedByParent ? "rpe-perm-row-blocked" : ""}`}
                      >
                        <div className="rpe-perm-info">
                          <span className="rpe-perm-name">{feature.display_name}</span>
                          {isViewFeature && (
                            <span className="rpe-perm-parent-sublabel">
                              Controls access to the whole module — turning this off also turns off everything below it
                            </span>
                          )}
                          {FEATURE_EXTRA_HINTS[feature.code] && (
                            <span className="rpe-perm-parent-sublabel">
                              {FEATURE_EXTRA_HINTS[feature.code]}
                            </span>
                          )}
                          {roleAccessMatrix.roles.length > 0 && (
                            <span className="rpe-perm-role-access">
                              Currently accessible to:{" "}
                              {currentlyAllowedRoles.length > 0
                                ? currentlyAllowedRoles.join(", ")
                                : "No roles"}
                            </span>
                          )}
                        </div>
                        <div className="rpe-perm-controls">
                          {EMPLOYEE_ROLE_ASSIGNING_FEATURE_CODES.includes(feature.code) && (
                            <div className="rpe-assignable-dropdown-wrap">
                              <button
                                type="button"
                                className={`rpe-assignable-trigger ${
                                  openAssignableRowCode === feature.code
                                    ? "rpe-assignable-trigger-open"
                                    : ""
                                }`}
                                disabled={!editable}
                                aria-expanded={openAssignableRowCode === feature.code}
                                title="Roles this role can assign"
                                onClick={(e) =>
                                  openAssignableDropdown(module, feature, e.currentTarget)
                                }
                              >
                                <span className="rpe-assignable-trigger-count">
                                  {assignableRoleIds.length}
                                </span>
                                <ChevronDown size={14} />
                              </button>

                              {openAssignableRowCode === feature.code &&
                                assignableDropdownStyle &&
                                createPortal(
                                  <AssignableRolesDropdown
                                    panelRef={assignableDropdownRef}
                                    style={assignableDropdownStyle}
                                    roles={assignableRoleOptions}
                                    selectedRoleIds={draftAssignableRoleIds || []}
                                    onToggleRole={toggleDraftAssignableRole}
                                    onCancel={cancelAssignableRoles}
                                    onConfirm={confirmAssignableRoles}
                                  />,
                                  document.body,
                                )}
                            </div>
                          )}

                          <button
                            type="button"
                            role="switch"
                            aria-checked={!!cell?.allowed}
                            aria-label={feature.display_name}
                            disabled={!editable}
                            title={tooltip}
                            className={`rpe-switch ${cell?.allowed ? "rpe-switch-on" : ""} ${
                              !editable ? "rpe-switch-disabled" : ""
                            }`}
                            onClick={(e) => togglePermission(module, feature, e.currentTarget)}
                          >
                            <span className="rpe-switch-knob" />
                          </button>
                        </div>
                      </div>

                      {childFeatures.map((child) => {
                        const childCell = matrix[child.code]?.[columnKey];
                        const parentAllowed = !!cell?.allowed;
                        const childEditable =
                          !readOnly &&
                          parentAllowed &&
                          isCellEditable(matrix, childCell, module, child, columnKey);
                        const childPersistedRoles = getDefaultRoleAccess(
                          roleAccessMatrix.matrix,
                          roleAccessMatrix.roles,
                          child.code,
                        );
                        const childCurrentRoles = editedRoleName
                          ? childCell?.allowed
                            ? Array.from(
                                new Set([...childPersistedRoles, editedRoleName]),
                              ).sort()
                            : childPersistedRoles.filter((name) => name !== editedRoleName)
                          : childPersistedRoles;
                        return (
                          <div
                            key={child.code}
                            className={`rpe-perm-row rpe-perm-row-secondary ${
                              !parentAllowed ? "rpe-perm-row-blocked" : ""
                            }`}
                          >
                            <div className="rpe-perm-info">
                              <span className="rpe-perm-name">{child.display_name}</span>
                              {roleAccessMatrix.roles.length > 0 && (
                                <span className="rpe-perm-role-access">
                                  Currently accessible to:{" "}
                                  {childCurrentRoles.length > 0
                                    ? childCurrentRoles.join(", ")
                                    : "No roles"}
                                </span>
                              )}
                            </div>
                            <div className="rpe-perm-controls">
                              <button
                                type="button"
                                role="switch"
                                aria-checked={!!childCell?.allowed}
                                aria-label={child.display_name}
                                disabled={!childEditable}
                                title={
                                  parentAllowed
                                    ? undefined
                                    : `Enable "${feature.display_name}" first`
                                }
                                className={`rpe-switch ${
                                  childCell?.allowed ? "rpe-switch-on" : ""
                                } ${!childEditable ? "rpe-switch-disabled" : ""}`}
                                onClick={(e) =>
                                  togglePermission(module, child, e.currentTarget)
                                }
                              >
                                <span className="rpe-switch-knob" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      </React.Fragment>
                    );
                  })}

              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  const renderContent = () => {
    switch (apiStatus) {
      case apiStatusConstants.inProgress:
      case apiStatusConstants.initial:
        return <LoadingView />;
      case apiStatusConstants.failure:
        return <FailureView onClickRetry={load} />;
      case apiStatusConstants.success:
        return renderEditor();
      default:
        return null;
    }
  };

  return (
    <div>
      <Header />
      <div className="d-flex">
        <LeftNavBar />
        <div className="page-content-wrapper rpe-page-content">
          <PageHeader>
            <PageHeaderName>
              <ArrowLeft
                size={20}
                onClick={handleCancel}
                title="Role Management"
                style={{ cursor: "pointer", marginRight: 8, flexShrink: 0 }}
              />
              {pageTitle}
            </PageHeaderName>
            <div className="rpe-header-actions">
              <SecondaryButton onClick={handleCancel} disabled={isSaving}>
                Cancel
              </SecondaryButton>
              {!readOnly && (
                <>
                  <PrimaryButton
                    width="150px"
                    onClick={handleSave}
                    disabled={isSaving || !canSave || apiStatus !== apiStatusConstants.success}
                  >
                    {isSaving ? "Saving..." : "Save Changes"}
                  </PrimaryButton>
                </>
              )}
            </div>
          </PageHeader>

          <div className="rpe-container" style={{ "--rpe-accent": accentColor }}>
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RolePermissionEditor;
